import { pendingThreadAttachmentsAtom } from "@/atoms/messagesAtoms.ts";
import { uploadThenPost } from "@/components/messages/Omnibox/attachmentSend.ts";
import type { AttachedFile } from "@/components/messages/Omnibox/types.ts";
import { api } from "@/lib/http.ts";
import { messageQueries } from "@/shared/messageQueries.ts";
import type { ArtifactDecisionsReq, ArtifactDecisionsResp } from "@/types/artifacts.ts";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSetAtom } from "jotai";

/**
 * Commit the staged review-queue decisions as one batch (one agent run).
 *
 * The staging store and request builders live in `@/shared/artifactDecisions`,
 * shared with native; only this mutation is web's, because it binds to web's
 * HTTP client.
 *
 * Files attached while answering a form upload first and travel as `fileIds`
 * on the same request (BIG-9261), so they land on the batch's one message. The
 * caller seeds their chips under `tempClientId` before committing.
 */
export function useCommitArtifactDecisions(threadId: number) {
  const queryClient = useQueryClient();
  const setPendingAttachments = useSetAtom(pendingThreadAttachmentsAtom);
  return useMutation({
    mutationFn: ({
      attachedFiles,
      ...req
    }: ArtifactDecisionsReq & { attachedFiles?: AttachedFile[] }) => {
      const url = `/api/a/threads/${threadId}/artifacts/decisions` as const;
      if (!attachedFiles?.length || req.tempClientId === undefined) {
        return api.post<ArtifactDecisionsResp>(url, req);
      }
      return uploadThenPost(
        threadId,
        req.tempClientId,
        attachedFiles,
        setPendingAttachments,
        (fileIds) => api.post<ArtifactDecisionsResp>(url, { ...req, fileIds }),
      );
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: messageQueries.thread(threadId) }),
  });
}
